import { cloneDeep } from "lodash";
import { connect } from "react-redux";
import { QuestionTable as Component } from "../QuestionTable";
import {
  questionTableSubmit,
  questionTableCellChange,
  questionTableShowHint,
  questionTableShowNextHint,
} from "./store/actions";

/**
 * mapStateToProps - This function maps the state from the Redux store to the component's props.
 * It allows us to extract specific data from the Redux store state and use them as props within our React component.
 *
 * @param {Object} state - The entire state of the Redux store.
 * @param {Object} ownProps - The current props of the component.
 *
 * @returns {Object} - Returns an object which populates the props of our component.
 */
function mapStateToProps(state, ownProps) {
  // Find the entry in the store state using the provided id.
  const entry = state.questionTable[ownProps.id];

  if (!entry) {
    // If no matching entry is found, return the original ownProps.
    return ownProps;
  }

  const disabledList = entry.disabled || [];
  const totalAttempts = ownProps.totalAttempts - disabledList.length;
  const complete = entry.correct || totalAttempts === 0;
  const showHint = Object.prototype.hasOwnProperty.call(entry, "showHint")
    ? entry.showHint
    : false;

  const updatedData = () => {
    // First create an immutable copy of the data object.
    const data = cloneDeep(ownProps.data);

    // Map over the question responses array and modify each item [rowId, colId] in the data to match the response values.
    entry.responses.map((response) => {
      data[response.rowId][response.colId] = {
        ...data[response.rowId][response.colId],
        value: response.value,
        showFeedback: response.showFeedback,
        disabled: response.disabled,
        answer: response.answer,
      };
    });

    return data;
  };

  return {
    ...ownProps,
    data: updatedData(),
    totalAttempts: totalAttempts,
    submitDisabled: entry.submitDisabled,
    correct: entry.correct,
    complete: complete,
    showHint: showHint,
    hints: entry.hints,
    hintIndex: entry.hintIndex,
  };
}

/**
 * mapDispatchToProps - Maps dispatch actions to the component's props.
 * This allows our React component to dispatch actions to modify the state in the Redux store.
 *
 * @param {Function} dispatch - The dispatch function from the Redux store.
 *
 * @returns {Object} - Returns an object with dispatch action functions.
 */
function mapDispatchToProps(dispatch) {
  return {
    // Dispatch the cscqSelectOption action.
    onCellChange: (params) => {
      dispatch(questionTableCellChange(params));
    },
    // Dispatch the questionTableSubmit action.
    submitAnswer: (id) => {
      dispatch(questionTableSubmit(id));
    },
    // Dispatch the questionTableShowHint action.
    setShowHint: (params) => {
      dispatch(questionTableShowHint(params));
    },
    // Dispatch the questionTableShowNextHint action.
    showNextHint: (id) => {
      dispatch(questionTableShowNextHint(id));
    },
  };
}

// Connect our component to the Redux store.
// This HOC (Higher Order Component) provides our component with props from the store state (mapStateToProps) and dispatch actions (mapDispatchToProps).
export const QuestionTableRedux = connect(
  mapStateToProps,
  mapDispatchToProps
)(Component);
